import React, { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import AddIcon from '@mui/icons-material/Add';
import ShareIcon from '@mui/icons-material/Share';
import LinkIcon from '@mui/icons-material/Link';

import {
  ILegalTransactionTalkRoomDraft,
  ITalkParticipantDraft,
  createTalkRoomDraft,
} from '../../../Interfaces/ILegalTransactionTalkRoom';
import { ILegalClient } from '../../../Interfaces/ILegalClient';
import { ILegalTransactionMeeting } from '../../../Interfaces/ILegalTransactionMeeting';
import { ParticipantsDialog } from './LegalTransactionTalkRoomParticipantsDialog';
import { useGetTalkRoomParticipantCounts } from '../../../hooks/reactQuerys/LegalTransactionTalkRoom';
import { CustomeTextField } from '../../../generic/CustomeTextField';
import {
  getInvitedMeetingLegalClients,
  getVisibleTalkParticipants,
} from './talkRoomDisplay';

interface IProps {
  legalClients: ILegalClient[];
  meetingArray: ILegalTransactionMeeting[];
  idLegalTransaction: number;
  talkRooms: ILegalTransactionTalkRoomDraft[];
  onChangeTalkRooms: (rooms: ILegalTransactionTalkRoomDraft[]) => void;
  disabled?: boolean;
}

export const LegalTransactionTalkRoomOverview: React.FC<IProps> = ({
  legalClients,
  meetingArray,
  idLegalTransaction,
  talkRooms,
  onChangeTalkRooms,
  disabled = false,
}) => {
  const [openCreate, setOpenCreate] = useState(false);
  const [roomName, setRoomName] = useState('');

  const [openParticipants, setOpenParticipants] = useState(false);
  const [activeRoomId, setActiveRoomId] = useState<number | null>(null);

  const sortedRooms = useMemo(() => {
    return [...talkRooms]
      .filter(r => r._state !== 'deleted')
      .sort((a, b) => {
        const aDate = a.CreatedAt ? new Date(a.CreatedAt).getTime() : 0;
        const bDate = b.CreatedAt ? new Date(b.CreatedAt).getTime() : 0;
        return aDate < bDate ? 1 : -1;
      });
  }, [talkRooms]);

  const markUpdated = (room: ILegalTransactionTalkRoomDraft) => {
    if (room._state === 'new' || room._state === 'deleted') return room;
    return { ...room, _state: 'updated' as const };
  };

  const roomIds = useMemo(
    () =>
      talkRooms
        .filter(r => r._state !== 'deleted')
        .map(r => r.idLegalTransactionTalkRoom),
    [talkRooms]
  );

  const { participantsById } = useGetTalkRoomParticipantCounts(roomIds, true);

  const handleCreateLocal = () => {
    if (disabled) return;
    if (!roomName.trim()) return;

    const newRoom = createTalkRoomDraft({
      talkRooms,
      idLegalTransaction,
      roomName,
    });

    onChangeTalkRooms([...talkRooms, newRoom]);
    setOpenCreate(false);
    setRoomName('');
  };

  const handleDeleteLocal = (id: number) => {
    if (disabled) return;

    const room = talkRooms.find(r => r.idLegalTransactionTalkRoom === id);
    if (!room) return;

    if (room._state === 'new') {
      onChangeTalkRooms(
        talkRooms.filter(r => r.idLegalTransactionTalkRoom !== id)
      );
      return;
    }

    onChangeTalkRooms(
      talkRooms.map(r =>
        r.idLegalTransactionTalkRoom === id ? { ...r, _state: 'deleted' } : r
      )
    );
  };

  const handleRenameLocal = (id: number, newName: string) => {
    if (disabled) return;

    onChangeTalkRooms(
      talkRooms.map(r =>
        r.idLegalTransactionTalkRoom === id
          ? markUpdated({ ...r, RoomName: newName })
          : r
      )
    );
  };

  const openParticipantsDialog = (id: number) => {
    setActiveRoomId(id);
    setOpenParticipants(true);
  };

  const activeRoom = useMemo(() => {
    if (activeRoomId === null) return null;
    return (
      talkRooms.find(r => r.idLegalTransactionTalkRoom === activeRoomId) ?? null
    );
  }, [activeRoomId, talkRooms]);

  const activeRoomInvitedLegalClients = useMemo(() => {
    if (!activeRoom) return [];

    return getInvitedMeetingLegalClients(
      meetingArray,
      activeRoom.idLegalTransactionTalkRoom
    );
  }, [activeRoom, meetingArray]);

  const updateActiveRoomParticipants = (
    participants: ITalkParticipantDraft[]
  ) => {
    if (!activeRoom) return;

    onChangeTalkRooms(
      talkRooms.map(r =>
        r.idLegalTransactionTalkRoom === activeRoom.idLegalTransactionTalkRoom
          ? markUpdated({ ...r, Participants: participants })
          : r
      )
    );
  };

  const copyJoinLink = async (joinUrl: string | null) => {
    if (!joinUrl) return;

    try {
      await navigator.clipboard.writeText(joinUrl);
      return;
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = joinUrl;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
      } finally {
        document.body.removeChild(textarea);
      }
    }
  };

  return (
    <>
      <Typography
        variant='h6'
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pt: 2,
        }}
      >
        Online Meeting Räume
        <IconButton
          onClick={() => setOpenCreate(true)}
          disabled={disabled}
          sx={{ marginLeft: 'auto' }}
          size='small'
        >
          <AddIcon />
        </IconButton>
      </Typography>

      <Dialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Neuer Meeting Raum</DialogTitle>
        <DialogContent
          sx={{
            display: 'grid',
            gap: 2,
            mt: 2,
            overflow: 'visible',
          }}
        >
          <CustomeTextField
            label='Raumname'
            type='string'
            attr='roomName'
            object={{ roomName }}
            setObject={(o: { roomName: string }) => setRoomName(o.roomName)}
            disabled={disabled}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Abbrechen</Button>
          <Button
            onClick={handleCreateLocal}
            variant='contained'
            disabled={disabled}
          >
            Hinzufügen
          </Button>
        </DialogActions>
      </Dialog>

      <ParticipantsDialog
        open={openParticipants}
        onClose={() => setOpenParticipants(false)}
        room={activeRoom}
        disabled={disabled}
        onChangeParticipants={updateActiveRoomParticipants}
        legalClients={legalClients}
        invitedLegalClients={activeRoomInvitedLegalClients}
      />

      <Table sx={{ mt: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Erstellt</TableCell>
            <TableCell>Raumname</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align='right'>Aktionen</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {sortedRooms.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5}>
                <i>Keine Meeting-Räume vorhanden.</i>
              </TableCell>
            </TableRow>
          ) : (
            sortedRooms.map(r => (
              <TableRow key={`talkroom-${r.idLegalTransactionTalkRoom}`}>
                <TableCell>
                  {r.CreatedAt ? new Date(r.CreatedAt).toLocaleString() : '-'}
                </TableCell>

                <TableCell>
                  <CustomeTextField
                    label='Raumname'
                    type='string'
                    attr='RoomName'
                    object={r}
                    setObject={(o: ILegalTransactionTalkRoomDraft) =>
                      handleRenameLocal(
                        r.idLegalTransactionTalkRoom,
                        o.RoomName
                      )
                    }
                    disabled={disabled || r._state === 'deleted'}
                    size='small'
                  />
                </TableCell>

                <TableCell>
                  {(() => {
                    const invitedCount = getInvitedMeetingLegalClients(
                      meetingArray,
                      r.idLegalTransactionTalkRoom
                    ).length;
                    const talkCount =
                      r.idLegalTransactionTalkRoom < 1
                        ? 0
                        : getVisibleTalkParticipants(
                            participantsById[r.idLegalTransactionTalkRoom] ?? []
                          ).length;

                    return (
                      <Button
                        size='small'
                        variant='outlined'
                        startIcon={<GroupIcon />}
                        onClick={() =>
                          openParticipantsDialog(r.idLegalTransactionTalkRoom)
                        }
                        disabled={disabled || r._state === 'deleted'}
                        sx={{
                          minHeight: 40,
                          alignSelf: 'center',
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          lineHeight: 1.3,
                          px: 1.25,
                        }}
                      >
                        <span>
                          Eingeladen {invitedCount}
                          <br />
                          Im Raum {talkCount}
                        </span>
                      </Button>
                    );
                  })()}
                </TableCell>

                <TableCell align='right'>
                  <IconButton
                    size='small'
                    onClick={() =>
                      r.JoinUrl &&
                      window.open(r.JoinUrl, '_blank', 'noopener,noreferrer')
                    }
                    disabled={disabled || r._state === 'deleted' || !r.JoinUrl}
                    title='Meeting öffnen'
                  >
                    <LinkIcon fontSize='small' />
                  </IconButton>
                  <IconButton
                    size='small'
                    onClick={() => copyJoinLink(r.JoinUrl)}
                    disabled={disabled || r._state === 'deleted' || !r.JoinUrl}
                    title='Meeting-Link kopieren'
                  >
                    <ShareIcon fontSize='small' />
                  </IconButton>

                  <IconButton
                    size='small'
                    onClick={() =>
                      handleDeleteLocal(r.idLegalTransactionTalkRoom)
                    }
                    disabled={disabled}
                    title='Raum löschen'
                  >
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </>
  );
};
